import { useSectionAccess } from "../../../auth/useSectionAccess";
import { useEffect, useState } from "react";
import { PanelLoading } from "../../../components/Loading";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { newsApi, type ArticleSummary } from "../../../api/news";
import type { ApiError } from "../../../api/client";
import { Pagination } from "../../../components/Pagination";
import { useConfirm } from "../../../components/ConfirmDialog";
import { useToast } from "../../../components/Toast";

const PAGE_SIZE = 25;

export default function AdminNewsPage() {
  const { canWrite, canFull } = useSectionAccess("news");
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [items, setItems] = useState<ArticleSummary[] | null>(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const result = await newsApi.list({ locale: "en", page, size: PAGE_SIZE });
      setItems(result.content.filter(Boolean));
      setTotalPages(result.totalPages);
    } catch (err) {
      setError((err as ApiError).message);
    }
  }
  useEffect(() => { void load(); }, [page]);

  async function publish(id: string) { await newsApi.publish(id); await load(); }
  async function archive(id: string) { await newsApi.archive(id); await load(); }
  async function remove(a: ArticleSummary) {
    if (!(await confirm({ message: t("admin.news.list.confirmDelete", { title: a.title }), danger: true }))) return;
    try { await newsApi.delete(a.id); await load(); } catch (err) { toast.error((err as ApiError).message); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("admin.news.list.title")}</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {canWrite && <Link to="/admin/news/new" style={primary}>{t("admin.news.list.newArticle")}</Link>}
        </div>
      </header>
      {error && <div style={errorBox}>{error}</div>}
      {!items ? (
        <PanelLoading text={t("common.loading")} />
      ) : items.length === 0 ? (
        <p style={{ color: "var(--ink-3)" }}>{t("admin.news.list.empty")}</p>
      ) : (
        <table style={tbl}>
          <thead>
            <tr>
              <th style={th}>{t("admin.news.list.col.title")}</th>
              <th style={th}>{t("admin.news.list.col.slug")}</th>
              <th style={th}>{t("admin.news.list.col.category")}</th>
              <th style={th}>{t("admin.news.list.col.status")}</th>
              <th style={th}>{t("admin.news.list.col.published")}</th>
              <th style={{ ...th, textAlign: "right" }}>{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td style={td}>{a.title}</td>
                <td style={td}><code>{a.slug}</code></td>
                <td style={td}>{a.categorySlug}</td>
                <td style={td}><StatusBadge status={a.status} /></td>
                <td style={td}>{a.publishedAt ? new Date(a.publishedAt).toLocaleDateString(i18n.language) : "—"}</td>
                <td style={td}>
                  {canWrite && <Link to={`/admin/news/${a.id}`} style={linkBtn}>{t("admin.news.list.action.edit")}</Link>}
                  {a.status !== "PUBLISHED"
                    ? <button type="button" disabled={!canWrite} onClick={() => publish(a.id)} style={linkBtn}>{t("admin.news.list.action.publish")}</button>
                    : <button type="button" disabled={!canWrite} onClick={() => archive(a.id)} style={linkBtn}>{t("admin.news.list.action.archive")}</button>}
                  <button type="button" disabled={!canFull} onClick={() => remove(a)} style={dangerLink}>{t("admin.news.list.action.delete")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const palette: Record<string, { bg: string; fg: string }> = {
    DRAFT: { bg: "var(--surface-muted)", fg: "var(--ink-3)" },
    PUBLISHED: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    ARCHIVED: { bg: "var(--warn-bg)", fg: "var(--warn)" },
  };
  const c = palette[status] ?? palette.DRAFT;
  const labelKey = `admin.news.list.status.${status}`;
  const translated = t(labelKey);
  const label = translated === labelKey ? status : translated;
  return <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{label}</span>;
}

const tbl: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13, background: "var(--surface)", borderRadius: 8, overflow: "hidden", boxShadow: "var(--shadow-1)" };
const th: React.CSSProperties = { textAlign: "left", padding: "10px 12px", background: "var(--surface-2)", color: "var(--ink-3)", fontWeight: 600, borderBottom: "1px solid var(--border)" };
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)" };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary-600)", color: "var(--on-primary)", textDecoration: "none", borderRadius: 6, fontSize: 14 };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-ink)", cursor: "pointer", marginRight: 8, fontSize: 13, textDecoration: "none" };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
